<template>
  <div class="note-card">
    <span class="menu" v-if="showActions" :class="{'hide-menu': menuOnHover}">
      <DropdownMenu :items="menu" @item-clicked="actionsClicked">
        <v-icon size="20">icon-dots-three-vertical</v-icon>
      </DropdownMenu>
    </span>
    <div class="card-content" :class="{'mini-preview': miniPreview}">
      <div v-html="note.content"
           class="content"
           ref="content"
           :class="{'prewrap': !isNoteHtml}"/>

      <div class="fadeout-wrapper"
           v-if="showFadeout">
        <div class="fadeout"
             :class="{'no-related': !showRelated, 'no-updated': !showUpdated}"/>
        <div class="show-more-btn">
          <button @click.stop="showMore" class="link-btn">{{$t('client.matters.matter_page.notes_tab.show_more')}}</button>
        </div>
      </div>

    </div>
    <v-divider v-if="showUpdated || showRelated"/>
    <div class="card-footer" v-if="showUpdated || showRelated">
      <div class="card-footer-row" v-if="showUpdated">
        <div class="staff-name" v-if="displayAuthor">{{staffName}}</div>
        <v-divider vertical v-if="displayAuthor" />
        <div class="value">{{note.updatedAt | date_and_hour}}</div>
      </div>
      <div class="card-footer-row" v-if="showRelated">
        <div class="label">{{$t('client.matters.matter_page.notes_tab.related_to')}}</div>
        <div class="value">
          <button @click.stop="navigateTo" class="link-btn">{{note.entity.title}}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {DropdownMenu} from "@/modules/common/components"
import {stringUtil} from '@/modules/common/utils'

export default {
  name: "NoteCard",
  props: {
    note: {
      type: Object,
      required: true
    },
    displayAuthor: {
      type: Boolean,
      default: true
    },
    showActions: {
      type: Boolean,
      default: true
    },
    menuOnHover: {
      type: Boolean,
      default: true
    },
    showUpdated: {
      type: Boolean,
      default: true
    },
    miniPreview: {
      type: Boolean,
      default: false
    },
    staffName: {
      type: String,
      required: false
    }
  },
  components: {
    DropdownMenu
  },
  data() {
    return {
      menu: [
        {title: this.$t('client.matters.matter_page.notes_tab.menu.edit'), action: 'edit'},
        {title: this.$t('client.matters.matter_page.notes_tab.menu.clone'), action: 'clone'},
        {title: this.$t('client.matters.matter_page.notes_tab.menu.remove'), action: 'remove'},
      ],
      showFadeout: false
    }
  },
  mounted() {
    this.presentReadMore();
    const contentEl = this.$refs.content;
    this.anchors = contentEl.querySelectorAll('a[href]');
    const frontageApi = this.$frontageApi;
    const isMobileApp = this.isMobileApp;
    this.anchors.forEach(a => {
      a.addEventListener('click', e => {
        e.stopPropagation();
        if (isMobileApp) {
          e.preventDefault();
          frontageApi.openExternal(e.target.attributes.href.value)
        }
      })
    })
  },
  updated() {
    this.presentReadMore();
  },
  computed: {
    isMeeting() {
      return this.note.entity && this.note.entity.type === 'meeting'
    },
    isPayment() {
      return this.note.entity && this.note.entity.type === 'payment'
    },
    showRelated() {
      if (!this.note.entity) return false
      return this.isPayment ? this.$hasPermission('can_view_payments') : true
    },
    isNoteHtml() {
      return stringUtil.isHTML(this.note.content)
    },
    isMobileApp() {
      return this.$store.getters.isMobileApp
    },
  },
  methods: {
      presentReadMore() {
        const contentEl = this.$refs.content;
        if (!contentEl) return;
        this.showFadeout = contentEl.offsetHeight < contentEl.scrollHeight;
      },
      navigateTo() {
        if (this.isMeeting) {
        this.$frontageApi.navigateTo('app.appointment.show', {uid: this.note.entity.uid})
      } else if (this.isPayment){
        this.$frontageApi.navigateTo('app.payment.show', {uid: this.note.entity.uid})
      }
    },
    actionsClicked({action}) {
      this.$emit('action', {action, noteUid: this.note.uid, page: this.note.page})
    },
    showMore() {
      this.actionsClicked({action: "edit"})
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/abstract/_variables';
@import '@/assets/scss/abstract/_i18n-mixins';

.note-card {
  width: 100%;
  padding: 14px 14px 8px 14px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  border: solid 1px $grey-color-2;
  clip-path: polygon( calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%, 0 0);

  &:before {
    content: "";
    width: 12px;
    height: 12px;
    position: absolute;
    top: 0;
    right: 0;
    background-color: $grey-color-2;
  }

  .menu {
    position: absolute;
    top: 8px;
    @include i-position-mirror('right', 8px);

    &.hide-menu {
      opacity: 0;
    }
  }

  &:hover {
    background-color: $grey-color-10;

    .menu.hide-menu {
      opacity: 1;
    }

    .card-content {

      .fadeout-wrapper {

        .fadeout {
          background: -webkit-linear-gradient(rgba(255, 255, 255, 0) 0%, $grey-color-10 100%);
          background-image: -moz-linear-gradient(rgba(255, 255, 255, 0) 0%, $grey-color-10 100%);
          background-image: -o-linear-gradient(rgba(255, 255, 255, 0) 0%, $grey-color-10 100%);
          background-image: linear-gradient(rgba(255, 255, 255, 0) 0%, $grey-color-10 100%);
          background-image: -ms-linear-gradient(rgba(255, 255, 255, 0) 0%, $grey-color-10 100%);
        }
      }
    }
  }

  .card-content {
    flex: 1;
    margin: 0 0 8px 0;

    &:last-child {
      margin: 0;
    }

    .content {
      font-size: 12px;
      max-height: 108px;
      overflow: hidden;
      width: calc(100% - 20px);

      &.prewrap {
        white-space: pre-wrap;
      }
    }

    .fadeout-wrapper {

      .fadeout {
        position: absolute;
        bottom: 80px;
        width: calc(100% - 30px);
        height: 40px;
        background: -webkit-linear-gradient(rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
        background-image: -moz-linear-gradient(rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
        background-image: -o-linear-gradient(rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
        background-image: linear-gradient(rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
        background-image: -ms-linear-gradient(rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);

        &.no-related, &.no-updated {
          bottom: 60px;
        }
      }

      .show-more-btn {
        padding-top: 5px;
        font-family: var(--primary-font-family);
        font-size: 13px;
        font-weight: 600;
      }
    }

    &.mini-preview {
      .content {
        max-height: 54px;
      }

      .fadeout-wrapper {
        .fadeout {
          bottom: 30px;
        }
      }
    }
  }

  .card-footer {
    margin: 8px 0 0 0;
    font-size: 11px;
    color: $blue-color-2;
    font-family: var(--primary-font-family);

    .card-footer-row {
      display: flex;

      &:not(:last-child) {
        margin: 0 0 3px 0;
      }

      div, hr {
        @include i-margin-mirror(0, 5px);
      }

      .label {
        color: $grey-color-6;
      }
    }
  }

  .link-btn {
    color: $blue-color-4;
  }
}
</style>
